import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import _map from 'lodash/map';
import apiV2Service from '../api/api-v2.service';
import {toLocalJobExecution, toLocalScheduledJob} from './jobs.dataConverters';
import {
  JobExecutionStatus,
  JobQueue,
  JobResult,
  JobType,
  JobUI,
  RemoteJobExecution,
  RemoteJobStatus,
  RemoteScheduledJob,
  ScheduledJobData,
} from './jobs.types';
import {DateTime} from 'luxon';

const basePath = 'Jobs';
const jobExecutionBasePath = 'JobsExecution';

async function getJobsScheduled(jobType?: JobType, ref_obj_1?: string) {
  const [{data: jobs}] = await Promise.all([
    apiV2Service.executeGetRequest<RemoteScheduledJob[]>(`${basePath}/GetScheduledobs`, {
      ...(jobType && {jobType}),
      ...(ref_obj_1 && {ref_obj_1}),
    }),
  ]);

  return _map(jobs, (job) => toLocalScheduledJob(job));
}

async function deleteReport(reportId: number) {
  return await apiV2Service.executeDeleteRequest(`${basePath}/${reportId}`);
}

async function getExecutions<JobData = ScheduledJobData>({
  identity,
  jobType,
  enqueuedSince,
  enqueuedUntil,
  statuses,
}: {
  jobType?: JobType;
  identity: {
    scheduledJobId?: number;
    ref_obj_1?: number;
    ref_obj_2?: number;
    ref_obj_3?: number;
    executionId?: number;
  };
  enqueuedSince?: DateTime;
  enqueuedUntil?: DateTime;
  statuses?: JobExecutionStatus[];
}) {
  const [{data: jobs}, enums] = await Promise.all([
    apiV2Service.executeGetRequest<RemoteJobExecution[]>(`${basePath}/GetJobExecutions`, {
      ...(jobType && {jobType}),
      ...identity,
      ...(enqueuedSince && {enqueuedSince: enqueuedSince.toISO()}),
      ...(enqueuedUntil && {enqueuedUntil: enqueuedUntil.toISO()}),
      ...(statuses && {statuses}),
    }),
    enumsService.loadEnums(CLEnumType.ExcelReports),
  ]);

  return _map(jobs, (job) => toLocalJobExecution<JobData>(job, enums));
}

async function getJobStatus(jobId: string) {
  const {data: jobStatus} = await apiV2Service.executeGetRequest<RemoteJobStatus>(
    `${basePath}/GetJobStatus/${jobId}`,
  );

  return jobStatus;
}

function getJobExecution<ExpectedJobResult extends JobResult>(jobId: number) {
  return apiV2Service
    .executeGetRequest<JobUI<ExpectedJobResult>>(`${jobExecutionBasePath}/${jobId}`)
    .then(({data}) => data);
}

async function enqueueScheduledJob(scheduledJobId: number) {
  return apiV2Service.executePostRequest(`${basePath}/EnqueueScheduledJob/${scheduledJobId}`);
}

async function updateStatus({scheduledJobId, status}: {scheduledJobId: number; status: number}) {
  return await apiV2Service.executePutRequest(
    `${basePath}/UpdateScheduledJobStatus/${scheduledJobId}`,
    {},
    {
      status,
    },
  );
}

function getAllJobsStatus() {
  return apiV2Service
    .executeGetRequest<
      (JobUI<JobResult> & {executionId: number})[]
    >(`${jobExecutionBasePath}/MyRecentExecutions`, {}, {includeClientId: false})
    .then((res) => res.data);
}

async function killJob(executionId: number) {
  const {data} = await apiV2Service.executePostRequest<boolean>(
    `${jobExecutionBasePath}/${executionId}/kill`,
  );

  return data;
}

async function queueJob({executionId, queue}: {executionId: number; queue: JobQueue}) {
  const {data} = await apiV2Service.executePostRequest<boolean>(
    `${jobExecutionBasePath}/${executionId}/enqueue/${queue}`,
  );

  return data;
}

export const jobsApi = {
  deleteReport,
  getExecutions,
  enqueueScheduledJob,
  getJobStatus,
  getJobsScheduled,
  updateStatus,
  getAllJobsStatus,
  getJobExecution,
  killJob,
  queueJob,
};
